/**
 * AuthBootstrap — wraps the app, calls GET /api/auth/me once on mount
 * to hydrate the store with the live session state. Until /me resolves
 * the store stays `loading` (or rehydrated from persisted state, if
 * any). After /me lands the store flips to `authenticated` (200) or
 * `unauthenticated` (genuine 401/403).
 *
 * Cold-load hardening (2026-06-22): the first /me on a fresh tab races
 * the gateway coming up, a flaky network, or a transient 5xx/429. The
 * old code logged the user out on ANY failure (including a network
 * blip), wiping a perfectly valid session. We now retry /me a few times
 * for TRANSIENT failures only, and on exhaustion we end the loading
 * state WITHOUT going `unauthenticated` (see setBootstrapDeferred). A
 * DEFINITIVE 401/403 is never retried and still logs the user out
 * immediately — that path is unchanged.
 *
 * The component renders its children unconditionally — route guards
 * read `useAuthStore().status` to decide whether to redirect.
 */
import { type ReactNode, useEffect } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { ApiError, authApi, queryClient } from './api';
import { cardLiveStateTransport } from './api/live-state';
import {
  setVerifiedPrincipalCacheResetter,
  startVerifiedPrincipalCoherence,
} from './verified-principal';

/** Attempts (incl. the first) and the backoff BEFORE attempts 2 and 3. */
const ME_MAX_ATTEMPTS = 3;
const ME_BACKOFF_MS = [300, 800];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * A failure is TRANSIENT (worth retrying / not a real logout) when it is
 * NOT a definitive auth verdict. Definitive = an ApiError carrying a
 * truthy 401/403 status — only those mean "this session is invalid".
 * Everything else — a network error (ApiError.status is falsy/0 on a
 * failed fetch, or a non-ApiError is thrown), a 5xx, or a 429 — is a
 * blip we should retry and, if it persists, NOT treat as logout.
 */
function isTransientFailure(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return false; // definitive
    if (!err.status) return true; // network failure: status 0/undefined
    if (err.status >= 500) return true; // server error
    if (err.status === 429) return true; // rate-limited
    return true; // any other non-auth status: do not log out on it
  }
  return true; // non-ApiError throw (network/timeout/parse) → transient
}

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const setBootstrapDeferred = useAuthStore((s) => s.setBootstrapDeferred);

  useEffect(() => {
    let cancelled = false;
    const unsetCacheResetter = setVerifiedPrincipalCacheResetter(() => {
      // The C2 validator/external-store cache lives outside QueryClient. Clear
      // and abort it first so a late response for principal A cannot populate
      // the freshly-cleared cache after the cookie changes to principal B.
      cardLiveStateTransport.clear();
      queryClient.clear();
    });
    const stopCoherence = startVerifiedPrincipalCoherence();
    setLoading();
    (async () => {
      for (let attempt = 0; attempt < ME_MAX_ATTEMPTS; attempt += 1) {
        try {
          const me = await authApi.me();
          if (!cancelled) setSession(me);
          return;
        } catch (err) {
          if (cancelled) return;
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            // Genuine logout — definitive, never retried. UNCHANGED path:
            // flip to `unauthenticated` → reactive redirect to /login.
            setUnauthenticated();
            return;
          }
          if (!isTransientFailure(err)) {
            // Defensive: any other non-transient verdict also logs out.
            setUnauthenticated();
            return;
          }
          // Transient: back off and retry while attempts remain.
          const backoff = ME_BACKOFF_MS[attempt];
          if (attempt < ME_MAX_ATTEMPTS - 1 && backoff !== undefined) {
            await sleep(backoff);
            if (cancelled) return;
            continue;
          }
          // Retries exhausted on a transient failure: do NOT log out and
          // do NOT wipe the persisted user. Keep a rehydrated session
          // alive (the cookie survives a blip); if none, stay loading.
          setBootstrapDeferred();
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
      stopCoherence();
      unsetCacheResetter();
    };
  }, [setLoading, setSession, setUnauthenticated, setBootstrapDeferred]);

  return <>{children}</>;
}
